import { Injectable, type OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs/internal/Subscription';
import { merge } from 'rxjs/internal/observable/merge';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { PAGE_HEADER_VERSION_STATE } from './page-header.config';
import { NPageHeader } from './page-header.interface';

@Injectable({
    providedIn: 'root'
})
export class PageHeaderService implements OnDestroy {
    state$ = new BehaviorSubject<NPageHeader.IVersionState | undefined>(undefined);
    highPriorityState$ = new BehaviorSubject<NPageHeader.IVersionState | undefined>(undefined);
    lowPriorityState$ = new BehaviorSubject<NPageHeader.IVersionState | undefined>(undefined);

    protected subscriptions: Subscription = new Subscription();

    constructor() {
        this.subscriptions.add(
            merge(this.lowPriorityState$, this.highPriorityState$)
                .pipe()
                .subscribe(() => this.setState())
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    setState(): void {
        if (this.highPriorityState$.value) {
            this.state$.next(this.highPriorityState$.value);
        } else if (this.lowPriorityState$.value) {
            this.state$.next(this.lowPriorityState$.value);
        } else {
            this.state$.next(PAGE_HEADER_VERSION_STATE);
        }
    }

    getState(): Observable<NPageHeader.IVersionState | undefined> {
        return this.state$.pipe(
            distinctUntilChanged(
                (prev, curr) => prev?.transparent === curr?.transparent && prev?.lightMode === curr?.lightMode && prev?.hideUp === curr?.hideUp
            )
        );
    }

    setLowPriorityState(state: NPageHeader.IVersionState) {
        if (state && (state.transparent !== undefined || state.lightMode !== undefined || state.hideUp !== undefined)) {
            this.lowPriorityState$.next(state);
        }
    }

    resetLowPriorityState() {
        this.lowPriorityState$.next(undefined);
    }

    setHighPriorityState(state: NPageHeader.IVersionState) {
        if (state && (state.transparent !== undefined || state.lightMode !== undefined || state.hideUp !== undefined)) {
            this.highPriorityState$.next(state);
        }
    }

    resetHighPriorityState() {
        this.highPriorityState$.next(undefined);
    }

    resetState() {
        this.resetLowPriorityState();
        this.resetHighPriorityState();
    }
}
